/**
 * The six leg counts, shared by the create form and the tournament settings
 * tab so the two cannot drift apart.
 *
 * ALL SIX ARE ALWAYS RENDERED. Nothing here keys off the expected entrant
 * count: entries are still arriving when the night is created, and a field
 * hidden because 12 players were expected is a field nobody can reach when 34
 * turn up. The rounds a bracket does not use simply go unread.
 *
 * The group count sits outside the disclosure because it is required —
 * group_best_of_legs is NOT NULL with no schema default, unlike the five
 * knockout rounds, which all default to 3 and which most nights never touch.
 */

import { useState } from 'react';

/**
 * The knockout rounds, in bracket order, mapped to the field names the API
 * expects. Mirrors KNOCKOUT_LEG_FIELDS in functions/lib/tournaments.js.
 *
 * knockoutBestOfLegs — one number for the whole bracket — was retired in
 * migration 003 and is now REJECTED by the server rather than ignored, so
 * nothing here may send it.
 */
export const KNOCKOUT_ROUNDS = [
  { field: 'r32BestOfLegs',   label: 'R32' },
  { field: 'r16BestOfLegs',   label: 'R16' },
  { field: 'qfBestOfLegs',    label: 'Quarter-Final' },
  { field: 'sfBestOfLegs',    label: 'Semi-Final' },
  { field: 'finalBestOfLegs', label: 'Final' },
];

/** Every knockout round defaults to 3, matching schema.sql and HARD_DEFAULTS. */
export const blankLegCounts = () => ({
  groupBestOfLegs: 3,
  ...Object.fromEntries(KNOCKOUT_ROUNDS.map(({ field }) => [field, 3])),
});

export default function LegCountFields({ form, set, idPrefix = 'legs', disabled }) {
  const [open, setOpen] = useState(false);
  const panelId = `${idPrefix}-knockout`;

  return (
    <>
      <div className="hd-field">
        <label className="hd-label" htmlFor={`${idPrefix}-group`}>Group stage legs</label>
        <input
          id={`${idPrefix}-group`}
          className="hd-input"
          type="number"
          min="1"
          value={form.groupBestOfLegs}
          onChange={set('groupBestOfLegs')}
          disabled={disabled}
          required
        />
        <span className="hd-hint">Best of, applied to every group match.</span>
      </div>

      <div className="hd-disclosure">
        <button
          type="button"
          className="hd-disclosure-toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="hd-disclosure-caret" aria-hidden="true" />
          Knockout round leg counts
        </button>

        {/* `hidden` rather than unmounting: the inputs stay mounted so a value
            typed, then collapsed, is still submitted. */}
        <div id={panelId} className="hd-fields" hidden={!open}>
          {KNOCKOUT_ROUNDS.map(({ field, label }) => (
            <div className="hd-field" key={field}>
              <label className="hd-label" htmlFor={`${idPrefix}-${field}`}>{label}</label>
              <input
                id={`${idPrefix}-${field}`}
                className="hd-input"
                type="number"
                min="1"
                value={form[field]}
                onChange={set(field)}
                disabled={disabled}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
